import { CSSProperties, useEffect, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { todayISO } from '../../lib/format';
import clsx from 'clsx';
import type { DashGranularity } from './api';
import { periodRange } from './DateFilter';
import './periodCalendar.css';

const FMT = 'YYYY-MM-DD';
const WEEKDAYS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

type View = 'days' | 'months' | 'years';

interface Props {
  /** the chosen date, YYYY-MM-DD */
  value: string;
  /** the period the date belongs to */
  period: DashGranularity | null;
  /** the dates of that period, shaded as one band (inclusive) */
  range: { from: string; to: string } | null;
  /** the latest date that can be picked */
  max: string;
  /** a day (first = last) or a start and end the person picked */
  onPickRange: (from: string, to: string) => void;
  /** a whole period was picked (Day, Week, Month or Year mode): any date inside it */
  onPickPeriod?: (date: string) => void;
  onClose: () => void;
}

const cell = (n: number): CSSProperties => ({ '--n': n } as CSSProperties);

/**
 * The calendar behind the date box: days (Monday first, like the week filter), a month picker and a year picker,
 * the chosen period shaded as a band, today ringed, and dates after today dimmed. Escape or a click outside closes it.
 */
export function PeriodCalendar({ value, range, max, onPickRange, onPickPeriod, onClose, period }: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const [view, setView] = useState<View>(period === 'month' ? 'months' : period === 'year' ? 'years' : 'days');
  const [shown, setShown] = useState(() => dayjs(value).startOf('month'));
  const [anchor, setAnchor] = useState<string | null>(null);
  const [hover, setHover] = useState<string | null>(null);
  const [dir, setDir] = useState<'next' | 'prev'>('next');
  const today = todayISO();

  useEffect(() => {
    const away = (e: PointerEvent) => { if (ref.current && !ref.current.parentElement?.contains(e.target as Node)) onClose(); };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('pointerdown', away);
    document.addEventListener('keydown', esc);
    ref.current?.querySelector<HTMLElement>('[data-autofocus]')?.focus();
    return () => { document.removeEventListener('pointerdown', away); document.removeEventListener('keydown', esc); };
  }, [onClose]);

  const move = (n: number, unit: 'month' | 'year') => { setDir(n > 0 ? 'next' : 'prev'); setShown((s) => s.add(n, unit)); };
  const lead = (shown.day() + 6) % 7; // blanks before the 1st, Monday first
  // whole weeks: the days of the neighbouring months fill the first and last row, so a week that crosses a month end is complete
  const cells = Array.from({ length: Math.ceil((lead + shown.daysInMonth()) / 7) * 7 }, (_, i) => shown.add(i - lead, 'day').format(FMT));
  const yearBase = Math.floor(shown.year() / 12) * 12;
  const thisYear = dayjs(max).year();
  const chosen = dayjs(value);
  const periodStartYear = chosen.month() >= 3 ? chosen.year() : chosen.year() - 1;
  // while a range is being picked, the band follows the pointer; otherwise it is the current period
  const single = !!onPickPeriod && (period === 'day' || period === 'week');
  const hoverBand = single && period === 'week' && hover && hover <= max ? periodRange('week', hover) : null;
  const band = hoverBand ?? (anchor ? (() => { const e = hover ?? anchor; return anchor <= e ? { from: anchor, to: e } : { from: e, to: anchor }; })() : range);
  const pick = (d: string) => {
    if (single) { onPickPeriod!(d); return; }
    if (!anchor) { setAnchor(d); return; }
    setAnchor(null);
    onPickRange(anchor <= d ? anchor : d, anchor <= d ? d : anchor);
  };

  const step = view === 'years' ? 12 : view === 'months' ? 1 : 1;
  const stepUnit = view === 'days' ? 'month' : 'year';
  const nextDisabled = view === 'years' ? yearBase + 12 > thisYear : view === 'months' ? shown.year() >= thisYear : shown.add(1, 'month').isAfter(max);

  return (
    <div className="pc" ref={ref} role="dialog" aria-label="Choose a date">
      <div className="pc-head">
        <button type="button" className="pc-nav" aria-label={view === 'years' ? 'Earlier years' : view === 'months' ? 'Previous year' : 'Previous month'} onClick={() => move(-step, stepUnit)}>
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M15 5l-7 7 7 7" /></svg>
        </button>
        <button type="button" className="pc-title" aria-live="polite" onClick={() => setView(view === 'days' ? 'months' : view === 'months' ? 'years' : 'days')}>
          <span key={`${view}${shown.format('YYYY-MM')}`}>{view === 'years' ? `${yearBase} – ${yearBase + 11}` : view === 'months' ? shown.format('YYYY') : shown.format('MMMM YYYY')}</span>
          <svg viewBox="0 0 24 24" aria-hidden="true" className={clsx(view !== 'days' && 'up')}><path d="M6 9l6 6 6-6" /></svg>
        </button>
        <button type="button" className="pc-nav" aria-label={view === 'years' ? 'Later years' : view === 'months' ? 'Next year' : 'Next month'} disabled={nextDisabled} onClick={() => move(step, stepUnit)}>
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M9 5l7 7-7 7" /></svg>
        </button>
      </div>

      {view === 'days' ? (
        <div className={clsx('pc-body', dir)} key={shown.format('YYYY-MM')}>
          <div className="pc-week" aria-hidden="true">{WEEKDAYS.map((w) => <span key={w}>{w}</span>)}</div>
          <div className="pc-days" role="grid" aria-label={shown.format('MMMM YYYY')}>
            {cells.map((d, i) => {
              const inBand = !!band && d >= band.from && d <= band.to;
              return (
                <button
                  key={d} type="button" role="gridcell" disabled={d > max} style={cell(i)}
                  className={clsx('pc-day', (anchor ? d === anchor : d === value) && 'on', d === today && 'today', inBand && 'band', inBand && d === band!.from && 'band-start', inBand && d === band!.to && 'band-end', i % 7 === 6 && 'sun', d.slice(0, 7) !== shown.format('YYYY-MM') && 'out')}
                  aria-label={dayjs(d).format('dddd, D MMMM YYYY')} aria-pressed={anchor ? d === anchor : d === value}
                  data-autofocus={d === value ? '' : undefined}
                  onPointerEnter={() => setHover(d)} onClick={() => pick(d)}
                >{Number(d.slice(8))}</button>
              );
            })}
          </div>
        </div>
      ) : null}

      {view === 'months' ? (
        <div className="pc-grid" key={`m${shown.year()}`}>
          {MONTHS.map((m, i) => {
            const first = shown.month(i).startOf('month');
            return (
              <button key={m} type="button" disabled={first.format(FMT) > max} className={clsx('pc-cell', i === chosen.month() && shown.year() === chosen.year() && 'on')} style={cell(i)}
                onClick={() => { if (period === 'month' && onPickPeriod) onPickPeriod(first.format(FMT)); else { setShown(first); setView('days'); } }}>{m}</button>
            );
          })}
        </div>
      ) : null}

      {view === 'years' ? (
        <div className="pc-grid" key={`y${yearBase}`}>
          {Array.from({ length: 12 }, (_, i) => yearBase + i).map((y, i) => (
            <button key={y} type="button" disabled={period === 'year' ? `${y}-04-01` > max : y > thisYear} className={clsx('pc-cell', (period === 'year' ? y === periodStartYear : y === chosen.year()) && 'on')} style={cell(i)}
              onClick={() => { if (period === 'year' && onPickPeriod) onPickPeriod(`${y}-04-01`); else { setShown(shown.year(y)); setView('months'); } }}>{period === 'year' ? `${y}–${String((y + 1) % 100).padStart(2, '0')}` : y}</button>
          ))}
        </div>
      ) : null}

      <div className="pc-foot">
        <span className="pc-hint" aria-live="polite">{anchor ? 'Now pick the last day (or the same day again)' : period === 'day' ? 'Pick a day' : period === 'week' ? 'Pick any day to choose its week' : period === 'month' ? 'Pick a month' : period === 'year' ? 'Pick a financial year (April to March)' : 'Pick a day, or a first and last day'}</span>
        <button type="button" className="pc-today" onClick={() => (onPickPeriod ? onPickPeriod(today) : onPickRange(today, today))}>Today</button>
      </div>
    </div>
  );
}
